import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import useLocalStorage from "use-local-storage";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLazyQuery } from "@apollo/client";

import { PageHeader, Card, FormGroup, Button, Select, Input, Checkbox, showSnackbar } from "@cofano/cck";
import { Alert, Box, CircularProgress, Grid2 as Grid, Tabs, Tab, Switch, FormControlLabel, Typography } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import FleetSelect from "view/fleets/components/selects/FleetSelect";
import DateRangePicker from "components/cck/DateRangePicker";
import useTabs from "components/hooks/useTabs";

import { Language } from "imports/Utils";

import { DateUtils } from "utils/DateUtils";

import ShipSelect, { ShipSelectOption } from "./components/selects/ShipSelect";
import ShipEventsHorizontalBar from "./components/events/ShipEventsHorizontalBar";
import ShipEventsVerticalBar from "./components/events/ShipEventsVerticalBar";
import ShipEventsSummary from "./components/events/ShipEventsSummary";
import { filterShipEventReports, getShipDayTimeWindows, getShipWeekTimeWindows } from "./utils/ShipUtils";
import { shipEventSchema, ShipEventSchemaInput, ShipEventSchemaOutput } from "./utils/ship.schema";

import {
	ShipEventReportsDocument,
	ShipEventReportsPerFleetDocument,
} from "__generated__/view/ships/graphql/ship.queries.generated";
import { ShipEventsEventReportsForPeriodFragment } from "__generated__/view/ships/graphql/ship.fragments.generated";

enum SearchMode {
	Ship = "ship",
	Fleet = "Fleet"
}

enum ShipEventTabs {
	Timeline = "timeline",
	Activity = "activity"
}

type Config = {
	satisfyOnContractHours: boolean;
	contractHours: {
		shipId: number;
		hours: number | null;
	}[];
};

export type ContractHours = {
	ship: ShipSelectOption;
	hours: number | null;
}[];

const ShipEvents = () => {

	const [ config, setConfig ] = useLocalStorage<Config>("shipEventsConfiguration", {
		satisfyOnContractHours: true,
		contractHours: [],
	});

	const [ searchMode, setSearchMode ] = useState(SearchMode.Ship);

	const [ response, setResponse ] = useState<{
		shipEventReports: ShipEventsEventReportsForPeriodFragment[],
		contractHours: ContractHours,
	}>({
		shipEventReports: [],
		contractHours: [],
	});

	const { control, handleSubmit, watch } = useForm<ShipEventSchemaInput, unknown, ShipEventSchemaOutput>({
		resolver: zodResolver(shipEventSchema),
		defaultValues: {
			ships: null,
			fleets: null,
			window: [ null, null ],
			satisfyOnContractHours: config.satisfyOnContractHours,
			contractHours: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "contractHours",
	});

	const values = watch();

	const [ getShipEventReports, { loading: shipEventReportsLoading } ] = useLazyQuery(ShipEventReportsDocument);
	const [ getShipEventReportsPerFleet, { loading: shipEventReportsPerFleetLoading } ] = useLazyQuery(ShipEventReportsPerFleetDocument);
	const loading = shipEventReportsLoading || shipEventReportsPerFleetLoading;

	const search = async (data: ShipEventSchemaOutput) => {
		setResponse({
			shipEventReports: [],
			contractHours: [],
		});

		switch (searchMode) {
			case SearchMode.Fleet: {
				const result = await getShipEventReportsPerFleet({
					variables: {
						fleetIds: data.fleets?.map(fleet => fleet.id) ?? [],
						timeWindows: getShipDayTimeWindows(data.window[0], data.window[1]),
					},
				});

				setResponse({
					shipEventReports: result.data?.fleetShipEventReports ?? [],
					contractHours: [],
				});

				break;
			}
			case SearchMode.Ship: {
				const result = await getShipEventReports({
					variables: {
						shipIds: data.ships?.map(ship => ship.id) ?? [],
						timeWindows: getShipWeekTimeWindows(data.window[0], data.window[1]),
					},
				});

				setResponse({
					shipEventReports: filterShipEventReports(
						result.data?.shipEventReports ?? [],
						data.contractHours,
						data.satisfyOnContractHours,
					),
					contractHours: data.contractHours,
				});
			}
		}
	};

	const saveConfiguration = () => {

		const currentContractHours = config.contractHours;

		values.contractHours.forEach(item => {

			const exist = currentContractHours.find(it => it.shipId === item.ship.id);

			if (exist) {
				const index = currentContractHours.findIndex(it => it.shipId === exist.shipId);
				currentContractHours[index].hours = item.hours;
			} else {
				currentContractHours.push({
					shipId: item.ship.id,
					hours: item.hours,
				});
			}
		});

		setConfig({
			satisfyOnContractHours: values.satisfyOnContractHours,
			contractHours: currentContractHours,
		});

		showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
	};

	useEffect(() => {

		const addedShips = values.ships?.filter(shipOption => !values.contractHours.find(c => c.ship.id === shipOption.id)) || [];
		const removedShips = values.contractHours.filter(c => !values.ships?.find(shipOption => shipOption.id === c.ship.id)).map(it => it.ship);

		addedShips.forEach(ship => {
			append({
				ship: ship,
				hours: config.contractHours.find(it => it.shipId === ship.id)?.hours || null,
			});
		});

		removedShips.forEach(ship => {
			const index = values.contractHours.findIndex(it => it.ship.id === ship.id);
			remove(index);
		});

	}, [ values.ships ]);

	const getMaxSelectableMoment = () => {
		switch (searchMode) {
			case SearchMode.Ship: {
				return values.window[0] ? moment(values.window[0]).add(59, "weeks").endOf("week") : null;
			}
			case SearchMode.Fleet: {
				return values.window[0] ? moment(values.window[0]).add(59, "days").endOf("day") : null;
			}
		}
	};

	return (
		<div className="ship-events">
			<PageHeader>
				<PageHeader.Title>
					{intl.formatMessage({ id: "SHIP_EVENTS" })}
					<Helmet title={intl.formatMessage({ id: "SHIP_EVENTS" })} />
				</PageHeader.Title>
			</PageHeader>

			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 3 }}>
					<Card
						title={intl.formatMessage({ id: "FILTER_TOOLBAR_FILTERS" })}
						actions={[ {
							render: () => (
								<FormControlLabel
									label={(
										<Typography
											variant="caption"
											fontSize="14px"
											fontWeight="medium"
											color="text.primary">
											{intl.formatMessage({ id: "PER_SHIP_OR_PER_FLEET" })}
										</Typography>
									)}
									labelPlacement="start"
									control={(
										<Switch
											sx={{ marginX: 0.5 }}
											checked={searchMode === SearchMode.Fleet}
											size="small"
											onChange={event => {
												setSearchMode(event.target.checked ? SearchMode.Fleet : SearchMode.Ship);
												setResponse({
													shipEventReports: [],
													contractHours: [],
												});
											}}
										/>
									)}
								/>
							),
						} ]}
						disablePadding
					>
						<form onSubmit={handleSubmit(search)}>
							<Box sx={{ borderTop: 1, borderColor: "divider", padding: 1.5 }}>
								<Box marginBottom={2}>
									{searchMode === SearchMode.Fleet ? (
										<FormGroup label={intl.formatMessage({ id: "FLEETS" })} overflow="hidden" required>
											<Controller
												key="fleets"
												name="fleets"
												control={control}
												render={({ field }) => (
													<FleetSelect {...field} multiple />
												)}
											/>
										</FormGroup>
									) : searchMode === SearchMode.Ship && (
										<FormGroup label={intl.formatMessage({ id: "SHIPS" })} overflow="hidden" required>
											<Controller
												key="ships"
												name="ships"
												control={control}
												render={({ field }) => (
													<ShipSelect {...field} multiple />
												)}
											/>
										</FormGroup>
									)}

									<FormGroup label={intl.formatMessage({ id: "START_END_WINDOW" })} overflow="hidden" required>
										<Controller
											name="window"
											control={control}
											render={({ field }) => (
												<DateRangePicker
													startDate={field.value[0] || undefined}
													endDate={field.value[1] || undefined}
													maxDate={DateUtils.getDateOrUndefined(getMaxSelectableMoment())}
													onChange={(value) => {
														if (searchMode === SearchMode.Fleet) {
															field.onChange(value);
														} else if (searchMode === SearchMode.Ship) {
															field.onChange([
																value[0] ? moment(value[0]).startOf("week").toDate() : null,
																value[1] ? moment(value[1]).endOf("week").toDate() : null,
															]);
														}
													}}
													showWeekPicker={searchMode === SearchMode.Ship}
												/>
											)}
										/>
									</FormGroup>
								</Box>

								{searchMode === SearchMode.Ship && (
									<Box mb={2}>
										<Typography
											variant="h4"
											fontWeight="medium"
											fontSize="14px"
											color="text.primary"
											mb={1}>
											{Language("CONFIGURATION")}
										</Typography>

										<FormGroup label={intl.formatMessage({ id: "SHIP_SATISFY_CONTRACT_HOURS" })} overflow="visible">
											<Controller
												name="satisfyOnContractHours"
												control={control}
												render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
										</FormGroup>

										{fields.length > 0 && (
											<Box mb={2}>
												<FormGroup label={intl.formatMessage({ id: "SHIP_ENTER_CONTRACT_HOURS" })} overflow="visible">
													<table
														style={{
															borderCollapse: "collapse",
															marginTop: "8px",
															width: "100%",
														}}>
														<thead>
															<tr>
																{[ "SHIPS", "SHIP_CONTRACT_HOURS" ].map(label => (
																	<th
																		style={{
																			borderBottom: "2px solid #DDD",
																			fontSize: "12px",
																			padding: "8px",
																		}}>
																		{intl.formatMessage({ id: label })}
																	</th>
																))}
															</tr>
														</thead>

														<tbody>
															{fields.map((row, index) => {
																const style = {
																	borderBottom: "1px solid #DDD",
																	fontSize: "12px",
																	padding: "8px",
																};
																return (
																	<tr key={row.id}>
																		<td style={style}>{fields[index].ship.name}</td>
																		<td style={style}>
																			<Controller
																				name={`contractHours.${index}.hours`}
																				control={control}
																				render={({ field }) => (
																					<Input
																						{...field}
																						value={field.value || undefined}
																						onChange={event => field.onChange(event.target.value || null)}
																						type="number"
																						step="1" />
																				)} />
																		</td>
																	</tr>
																);
															})}
														</tbody>
													</table>
												</FormGroup>
											</Box>
										)}
									</Box>
								)}

								<Box sx={{ display: "flex", justifyContent: "space-between" }}>
									<Button
										disabled={(searchMode === SearchMode.Ship && !values.ships)
											|| (searchMode === SearchMode.Fleet && !values.fleets)
											|| !values.window[0]
											|| !values.window[1]
											|| loading}
										loading={loading}
										type="submit"
										size="small">
										{Language("SEARCH")}
									</Button>

									{searchMode === SearchMode.Ship && (
										<Button onClick={() => saveConfiguration()} variant="text" size="small">
											{intl.formatMessage({ id: "SAVE" })}
											{"  "}
											{intl.formatMessage({ id: "CONFIGURATION" })}
										</Button>
									)}
								</Box>
							</Box>
						</form>
					</Card>
				</Grid>

				<Grid size={{ xs: 12, md: 9 }}>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<CircularProgress size={24} />
						</Box>
					) : response.shipEventReports.length > 0 ? (
						<Reports
							searchMode={searchMode}
							shipEventReports={response.shipEventReports}
							contractHours={response.contractHours}
						/>
					) : (
						<Alert severity="success" color="warning">
							{intl.formatMessage({ id: "NO_DATA_FOUND" })}
						</Alert>
					)}
				</Grid>
			</Grid>
		</div>
	);
};

const Reports = ({ searchMode, shipEventReports, contractHours }: {
	searchMode: SearchMode,
	shipEventReports: ShipEventsEventReportsForPeriodFragment[],
	contractHours: ContractHours
}) => {

	const options = [ ...shipEventReports ].map(it => ({
		window: it.period,
		ships: it.reports.map(r => r.ship.name),
	}));

	const [ selectedWindow, setSelectedWindow ] = useState<{ from: number; to: number } | null>(
		options[0]?.window.from && options[0]?.window.to ? {
			from: options[0].window.from,
			to: options[0].window.to,
		} : null,
	);

	const { selectedTab, selectTab } = useTabs<ShipEventTabs>({
		defaultSelected: ShipEventTabs.Timeline,
	});

	useEffect(() => {
		if (selectedTab === ShipEventTabs.Activity && searchMode === SearchMode.Fleet) {
			selectTab(ShipEventTabs.Timeline);
		}
	}, []);

	return (
		<Grid container direction="column" spacing={3}>
			<Grid size={12}>
				<Card>
					<Grid container spacing={3}>
						<Grid size={{ xs: 12, sm: 6, md: 4 }}>
							<FormGroup
								label={searchMode === SearchMode.Ship ? intl.formatMessage({ id: "CONTRACT_WEEK" }) : intl.formatMessage({ id: "CONTRACT_DAY" })}
								overflow="hidden"
							>
								<Select
									value={options.find(it => it.window.from === selectedWindow?.from)}
									onChange={option => {
										if (option?.window.from && option?.window.to) {
											setSelectedWindow({
												from: option.window.from,
												to: option.window.to,
											});
										}
									}}
									getOptionLabel={option => {
										switch (searchMode) {
											case SearchMode.Ship: {
												const window = `${moment(option.window.from).format("ddd DD MMM")} - ${moment(option.window.to).format("ddd DD MMM")}`;

												if (option.ships.length === 1) {
													return `${window} (${option.ships[0]})`;
												}
												return window;
											}
											case SearchMode.Fleet: {
												return moment(option.window.from).format("ddd DD MMM");
											}
										}
									}}
									fetchOptions={options}
									clearable={false}
									disableSearch
								/>
							</FormGroup>
						</Grid>
					</Grid>
				</Card>
			</Grid>

			<Grid size={12}>
				<ShipEventsSummary
					key={selectedWindow?.from}
					reports={shipEventReports.find(it => it.period.from === selectedWindow?.from)?.reports || []}
					contractHours={contractHours}
				/>
			</Grid>

			<Grid size={12}>
				<Card disablePadding>
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							variant="scrollable"
							value={selectedTab}
							onChange={(_event, newValue) => selectTab(newValue)}>
							<Tab label={Language("SHIP_ACTIONS_TIMELINE")} value={ShipEventTabs.Timeline} />
							{searchMode === SearchMode.Ship && (
								<Tab
									label={Language("SHIP_ACTIVITY_TIME_PER_DAY")}
									value={ShipEventTabs.Activity}
								/>
							)}
						</Tabs>
					</Box>

					{selectedWindow && (
						<Box padding={1.5}>
							{selectedTab === ShipEventTabs.Timeline && (
								<ShipEventsHorizontalBar
									timeWindow={selectedWindow}
									reports={shipEventReports.find(it => it.period.from === selectedWindow.from)?.reports ?? []}
								/>
							)}

							{selectedTab === ShipEventTabs.Activity && (
								<ShipEventsVerticalBar
									timeWindow={selectedWindow}
									reports={shipEventReports.find(it => it.period.from === selectedWindow.from)?.reports ?? []}
								/>
							)}
						</Box>
					)}
				</Card>
			</Grid>
		</Grid>
	);
};

export default ShipEvents;
